/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Validation status component for Labels Import
 *
 * @author Anton Panasenko
 * @version 1.3.1
 * @since 1.3.0
 */

import { Fragment, useEffect } from 'react';

import range from 'lodash/range';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../../assets';
import { LinkBlock } from '../../../../components/common';
import { ImgStyled } from '../../../../components/common/Block/Img';
import EllipseText from '../../../../components/common/EllipsisText/EllipsisText';
import { LoaderLinear } from '../../../../components/common/Loader/Loaders';
import { EFileExtension, MapExtensionToIcon } from '../../../../modules/app/appConstants';
import { ILabelsImportTranslation } from '../../../../modules/internationalization/types/wizards/labelsImport';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { getFileExtension } from '../../../../utils/formatText';
import { labelsImportLoadLabelsValidationStop } from '../../labelsImportActions';
import { LABELS_IMPORT_LINEAR_LOADERS_COUNT } from '../../labelsImportConstants';
import {
    ILabelsImportFileInfoItem,
    ILabelsImportLabelsValidationInfo,
} from '../../labelsImportReducer';
import {
    makeSelectLabelsImportFileInfoItems,
    makeSelectLabelsImportTotalRowsCount,
} from '../../labelsImportSelectors';
import { ILabelsImportLoadLabelsValidationStopPayload } from '../../labelsImportTypes';
import {
    LabelsImportValidationStatusFileContainerStyled as FileContainerStyled,
    LabelsImportValidationStatusFileMessageStyled as FileMessageStyled,
    LabelsImportValidationStatusFileNameStyled as FileNameStyled,
    LabelsImportValidationStatusInfoItemStyled as InfoItemStyled,
    LabelsImportValidationStatusLinkContainerStyled as LinkContainerStyled,
    LabelsImportValidationStatusProgressBarStyled as ProgressBarStyled,
    LabelsImportValidationStatusContainerStyled as ValidationStatusContainerStyled,
    LabelsImportValidationStatusLoaderContainerStyled as ValidationStatusLoaderContainerStyled,
} from '../styled/LabelsImportStepFinalStyled';

interface ILabelsImportValidationStatusProps {
    labelsImportTranslation: ILabelsImportTranslation;
    labelsValidationInfo: ILabelsImportLabelsValidationInfo;
}

export type TLabelsImportValidationStatusProps = ILabelsImportValidationStatusProps & IConnectedProps & IDispatchProps;

export const LabelsImportValidationStatus = (props: TLabelsImportValidationStatusProps) => {
    const { validationStatus: validationStatusTranslation } = props.labelsImportTranslation;
    const { subscriberCount, successRowsCount } = props.labelsValidationInfo;
    const fileExtension = getFileExtension(props.fileInfoItems[0]?.name) as EFileExtension;
    const fileIcon = fileExtension && MapExtensionToIcon[fileExtension] ? MapExtensionToIcon[fileExtension] :
        icons.defaultFileIcon;
    const fileTitle = props.fileInfoItems[0]?.name ?? '';
    const progressBarPercent = props.totalRowsCount ? Math.ceil((subscriberCount/props.totalRowsCount) * 100) : 0;

    useEffect(() => {
        return () => props.loadLabelsValidateStop({});
    }, [])

    const handleCancelClick = () => {
        props.loadLabelsValidateStop({ isInterrupt: true })
    }

    return (
        <Fragment>
            <ValidationStatusContainerStyled>
                <FileContainerStyled>
                    <ImgStyled
                        className={'medium'}
                        src={fileIcon}
                    />

                    <FileMessageStyled>
                        {validationStatusTranslation.fileProcess}&nbsp;
                    </FileMessageStyled>

                    <EllipseText
                        title={fileTitle}
                        wrapper={FileNameStyled}
                    >
                        {fileTitle}
                    </EllipseText>
                </FileContainerStyled>

                <ProgressBarStyled percent={progressBarPercent} />

                <InfoItemStyled>
                    <span>{validationStatusTranslation.rowsProcessed}</span>
                    <span>{subscriberCount}/{props.totalRowsCount}</span>
                </InfoItemStyled>
                <InfoItemStyled>
                    <span>{validationStatusTranslation.rowsErrors}</span>
                    <span>{subscriberCount - successRowsCount}</span>
                </InfoItemStyled>

                <LinkContainerStyled>
                    <LinkBlock onClick={handleCancelClick}>
                        {validationStatusTranslation.cancelButton}
                    </LinkBlock>
                </LinkContainerStyled>
            </ValidationStatusContainerStyled>

            {
                range(LABELS_IMPORT_LINEAR_LOADERS_COUNT).map((item, i) => (
                    <ValidationStatusLoaderContainerStyled key={i}>
                        <LoaderLinear maxWidth='75%' />
                    </ValidationStatusLoaderContainerStyled>
                ))
            }
        </Fragment>
    )
}

interface IConnectedProps {
    fileInfoItems: ILabelsImportFileInfoItem[];
    totalRowsCount: number;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    fileInfoItems: makeSelectLabelsImportFileInfoItems(),
    totalRowsCount: makeSelectLabelsImportTotalRowsCount(),
});

interface IDispatchProps {
    loadLabelsValidateStop: (payload: ILabelsImportLoadLabelsValidationStopPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadLabelsValidateStop: payload => dispatch(labelsImportLoadLabelsValidationStop(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LabelsImportValidationStatus);
